/**
 * Hook for streaming brief generation via SSE.
 *
 * Connects to the brief-generate-stream Function URL and yields sections
 * progressively as they arrive. The UI renders each card the moment its
 * section lands.
 */

import { useState, useCallback, useRef } from 'react';
import type { BriefSection, Tier1Inputs } from '../types/canvas';

export type GenerateStreamStatus = 'idle' | 'connecting' | 'streaming' | 'complete' | 'error';

export interface GenerateStreamState {
  status: GenerateStreamStatus;
  sessionId: string | null;
  sections: BriefSection[];
  failedIds: string[];
  error: string | null;
}

export interface UseGenerateStream {
  state: GenerateStreamState;
  start: (inputs: Tier1Inputs) => void;
  connect: (sessionId: string) => void;
  abort: () => void;
}

const GENERATE_STREAM_URL = import.meta.env.VITE_GENERATE_STREAM_URL ?? '/api/brief/generate-stream';

export function useGenerateStream(): UseGenerateStream {
  const [state, setState] = useState<GenerateStreamState>({
    status: 'idle',
    sessionId: null,
    sections: [],
    failedIds: [],
    error: null,
  });

  const abortRef = useRef<AbortController | null>(null);

  const consumeStream = useCallback(async (res: Response, controller: AbortController) => {
    const reader = res.body?.getReader();
    if (!reader) {
      setState((s) => ({ ...s, status: 'error', error: 'No response body' }));
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (controller.signal.aborted) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const json = line.slice(6);
        if (!json) continue;

        try {
          const event = JSON.parse(json);
          switch (event.type) {
            case 'open':
              setState((s) => ({
                ...s,
                status: 'streaming',
                sessionId: event.sessionId,
              }));
              break;
            case 'section':
              setState((s) => ({
                ...s,
                sections: [...s.sections, event.section],
              }));
              break;
            case 'complete':
              setState((s) => ({
                ...s,
                status: 'complete',
                sections: event.sections,
                failedIds: event.failedIds ?? [],
              }));
              break;
            case 'error':
              setState((s) => ({
                ...s,
                status: 'error',
                error: event.message,
              }));
              break;
          }
        } catch {
          // Malformed SSE line, skip
        }
      }
    }

    setState((s) => {
      if (s.status === 'streaming') {
        return { ...s, status: 'complete' };
      }
      return s;
    });
  }, []);

  const start = useCallback((inputs: Tier1Inputs) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState({
      status: 'connecting',
      sessionId: null,
      sections: [],
      failedIds: [],
      error: null,
    });

    (async () => {
      try {
        const res = await fetch(GENERATE_STREAM_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(inputs),
          signal: controller.signal,
        });

        if (!res.ok) {
          setState((s) => ({ ...s, status: 'error', error: `HTTP ${res.status}` }));
          return;
        }

        await consumeStream(res, controller);
      } catch (err: unknown) {
        if ((err as Error).name === 'AbortError') return;
        setState((s) => ({
          ...s,
          status: 'error',
          error: err instanceof Error ? err.message : 'Unknown error',
        }));
      }
    })();
  }, [consumeStream]);

  const connect = useCallback((sessionId: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState((s) => ({
      ...s,
      status: 'connecting',
      sessionId,
    }));

    (async () => {
      try {
        const res = await fetch(`${GENERATE_STREAM_URL}?sessionId=${sessionId}`, {
          method: 'GET',
          credentials: 'include',
          signal: controller.signal,
        });

        if (!res.ok) {
          setState((s) => ({ ...s, status: 'error', error: `HTTP ${res.status}` }));
          return;
        }

        await consumeStream(res, controller);
      } catch (err: unknown) {
        if ((err as Error).name === 'AbortError') return;
        setState((s) => ({
          ...s,
          status: 'error',
          error: err instanceof Error ? err.message : 'Unknown error',
        }));
      }
    })();
  }, [consumeStream]);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    setState((s) => ({ ...s, status: 'idle' }));
  }, []);

  return { state, start, connect, abort };
}
